import { staffFetch } from "./fetch";
import IUser from "../types/services/IUser";
import { resolveFirebaseIdTokenForApiCall } from "./firebaseToken";
import { suggestSubdomainFromName } from "./suggestSubdomainFromName";

export interface SuperadminCharityCreateRequest {
  name: string;
  subdomain: string;
  charityId: string;
  isPrime: boolean;
  highlightColor: string;
  charityUrl: string;
}

export interface SuperadminCharitySession {
  token: string;
  user: IUser;
}

export interface SuperadminCharityCreateResponse {
  charityId: string;
  status: string;
  currentStep?: number;
  failedStep?: number;
  partialState?: Record<string, unknown>;
  artifacts?: string[];
  session?: SuperadminCharitySession;
  errorMessage?: string;
}

export type AvailabilityStatus =
  | "available"
  | "live"
  | "provision_failed"
  | "invalid";

export interface CharityProvisionSuggestionsResponse {
  suggestedId?: string;
  suggestedSubdomain?: string;
  charityId?: string;
  suffixApplied?: boolean;
  status?: AvailabilityStatus;
  idStatus?: AvailabilityStatus;
  subdomainStatus?: AvailabilityStatus;
  failedStep?: number;
  artifacts?: string[];
  errorMessage?: string;
}

export interface CharityIdCheckResponse {
  status: AvailabilityStatus;
  idStatus?: AvailabilityStatus;
  subdomainStatus?: AvailabilityStatus;
  suggestedId?: string;
  failedStep?: number;
  artifacts?: string[];
  errorMessage?: string;
}

const CREATE_TIMEOUT_MS = 120000;

const KNOWN_PROVISION_ERRORS: Array<{ match: string; message: string }> = [
  {
    match: "Charity id is blocked by provision audit",
    message:
      "This charity ID has a previous failed setup with partial changes. Use a different ID or ask ops to clear the failed provision.",
  },
  {
    match: "Charity provision already in progress",
    message:
      "A setup for this charity ID is already running. Wait a few minutes or choose a different ID.",
  },
  {
    match: "Charity id or subdomain is not available",
    message:
      "That charity ID or domain is already in use. Choose different values.",
  },
  {
    match: "Invalid charity URL",
    message: "Enter a valid charity URL starting with http:// or https://.",
  },
  {
    match: "Invalid charity name",
    message:
      "Charity name can only contain letters, numbers, spaces, and hyphens.",
  },
  {
    match: "charityId must match",
    message:
      "Charity ID must be 1–40 characters using lowercase letters, numbers, and hyphens.",
  },
  {
    match: "Invalid subdomain",
    message: "Domain must use lowercase letters, numbers, and hyphens only.",
  },
  {
    match: "template_invalid",
    message:
      "The charity configuration template could not be loaded. Try again or contact support.",
  },
  {
    match: "Bitbucket master config.json is empty or missing",
    message:
      "Could not load the charity JSON template from Bitbucket. Check Bitbucket credentials in Redis and that master/config.json exists.",
  },
  {
    match: "Bitbucket credentials are not configured",
    message:
      "Bitbucket is not configured for this environment. Ask ops to set bitbucketEmail and bitbucketApiToken in Redis.",
  },
  {
    match: "Realm already exists",
    message:
      "This charity ID already exists in auth. Use a different ID or ask ops to remove the partial realm before retrying.",
  },
  {
    match: "500 Server Error",
    message:
      "A backend service returned an unexpected error during setup. Check admin-service logs for the failed step or try again.",
  },
  {
    match: "Bitbucket create branch",
    message: "", // passthrough — backend message is already actionable
  },
  {
    match: "Bitbucket commit config.json",
    message: "",
  },
  {
    match: "Auth service MySQL provision",
    message: "",
  },
  {
    match: "Previous charity setup failed",
    message: "",
  },
];

export function formatCharityProvisionError(
  err: unknown,
  fallback = "Could not create the charity. Try again or contact support."
): string {
  const raw =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!raw.trim()) {
    return fallback;
  }

  const normalized = raw.trim();
  const lower = normalized.toLowerCase();

  if (
    normalized === "Load failed" ||
    normalized === "Failed to fetch" ||
    lower.includes("networkerror") ||
    lower.includes("network request failed")
  ) {
    return "Could not reach the admin service. Check your connection and try again.";
  }

  if (err instanceof Error && err.name === "AbortError") {
    return "The request timed out. Provisioning may still be running — check status or try again shortly.";
  }

  const createFailedMatch = normalized.match(/^Create charity failed: (\d+)$/);
  if (createFailedMatch) {
    const status = createFailedMatch[1];
    if (status === "401" || status === "403") {
      return "You are not signed in or lack permission to create charities.";
    }
    if (status === "400") {
      return "The request was invalid. Check all fields and try again.";
    }
    if (status === "504") {
      return "The server is still working on this charity. Status polling will continue automatically.";
    }
    if (status === "500" || status === "502" || status === "503") {
      return "The server encountered an error while creating the charity. Try again or contact support.";
    }
    return `Could not create the charity (HTTP ${status}). Try again or contact support.`;
  }

  const statusFailedMatch = normalized.match(
    /^Provision status failed: (\d+)$/
  );
  if (statusFailedMatch) {
    return "Could not check provisioning progress. Try closing and reopening this dialog.";
  }

  for (const { match, message } of KNOWN_PROVISION_ERRORS) {
    if (normalized.includes(match)) {
      return message || normalized;
    }
  }

  if (normalized.startsWith("Provisioning failed at step")) {
    return normalized;
  }

  return normalized;
}

async function authQueryToken(token: string): Promise<string> {
  return resolveFirebaseIdTokenForApiCall(token);
}

function parseAvailabilityStatus(
  value: unknown
): AvailabilityStatus | undefined {
  if (
    value === "available" ||
    value === "live" ||
    value === "provision_failed" ||
    value === "invalid"
  ) {
    return value;
  }
  return undefined;
}

function normalizeProvisionSuggestions(
  body: Record<string, unknown>,
  name: string
): CharityProvisionSuggestionsResponse {
  const suggestedId =
    typeof body.suggestedId === "string" ? body.suggestedId : "";
  const charityId =
    typeof body.charityId === "string" && body.charityId
      ? body.charityId
      : suggestedId;
  let suggestedSubdomain =
    typeof body.suggestedSubdomain === "string" ? body.suggestedSubdomain : "";
  if (!suggestedSubdomain) {
    suggestedSubdomain = suggestSubdomainFromName(name);
  }
  const status = parseAvailabilityStatus(body.status);
  return {
    suggestedId,
    suggestedSubdomain,
    charityId,
    suffixApplied: Boolean(body.suffixApplied),
    status,
    idStatus: parseAvailabilityStatus(body.idStatus) ?? status,
    subdomainStatus: parseAvailabilityStatus(body.subdomainStatus) ?? status,
    failedStep:
      typeof body.failedStep === "number" ? body.failedStep : undefined,
    artifacts: Array.isArray(body.artifacts)
      ? (body.artifacts as string[])
      : undefined,
    errorMessage:
      typeof body.errorMessage === "string" && body.errorMessage
        ? body.errorMessage
        : undefined,
  };
}

export async function fetchProvisionSuggestions(
  adminServiceUrl: string,
  token: string,
  name: string
): Promise<CharityProvisionSuggestionsResponse> {
  const resolved = await authQueryToken(token);
  const url = `${adminServiceUrl}/api/v1/superadmin/charities/id-suggestion?name=${encodeURIComponent(
    name
  )}&token=${encodeURIComponent(resolved)}`;
  const response = await staffFetch(url, { method: "GET", mode: "cors" });
  if (!response.ok) {
    throw new Error(`Provision suggestions failed: ${response.status}`);
  }
  const body = (await response.json()) as Record<string, unknown>;
  return normalizeProvisionSuggestions(body, name);
}

export async function checkCharityId(
  adminServiceUrl: string,
  token: string,
  id: string,
  subdomain: string
): Promise<CharityIdCheckResponse> {
  const resolved = await authQueryToken(token);
  const url = `${adminServiceUrl}/api/v1/superadmin/charities/check-id?id=${encodeURIComponent(
    id
  )}&subdomain=${encodeURIComponent(subdomain)}&token=${encodeURIComponent(
    resolved
  )}`;
  const response = await staffFetch(url, { method: "GET", mode: "cors" });
  if (!response.ok) {
    throw new Error(`Check id failed: ${response.status}`);
  }
  return response.json() as Promise<CharityIdCheckResponse>;
}

export async function createSuperadminCharity(
  adminServiceUrl: string,
  token: string,
  body: SuperadminCharityCreateRequest,
  signal?: AbortSignal
): Promise<SuperadminCharityCreateResponse> {
  const resolved = await authQueryToken(token);
  const url = `${adminServiceUrl}/api/v1/superadmin/charities?token=${encodeURIComponent(
    resolved
  )}`;
  const controller = signal ? null : new AbortController();
  const effectiveSignal = signal || controller?.signal;
  const timeoutId = controller
    ? window.setTimeout(() => controller.abort(), CREATE_TIMEOUT_MS)
    : null;
  try {
    const response = await staffFetch(url, {
      method: "POST",
      mode: "cors",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal: effectiveSignal,
    });
    if (!response.ok && response.status !== 504) {
      let detail = "";
      try {
        const body = (await response.json()) as Record<string, unknown>;
        if (typeof body.errorMessage === "string" && body.errorMessage) {
          detail = body.errorMessage;
        } else if (typeof body.message === "string" && body.message) {
          detail = body.message;
        }
      } catch {
        // ignore non-JSON error bodies
      }
      throw new Error(detail || `Create charity failed: ${response.status}`);
    }
    try {
      return (await response.json()) as SuperadminCharityCreateResponse;
    } catch {
      throw new Error(
        "The server returned an unexpected response while creating the charity."
      );
    }
  } finally {
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
    }
  }
}

export async function getProvisionStatus(
  adminServiceUrl: string,
  token: string,
  charityId: string
): Promise<SuperadminCharityCreateResponse> {
  const resolved = await authQueryToken(token);
  const url = `${adminServiceUrl}/api/v1/superadmin/charities/provision/${encodeURIComponent(
    charityId
  )}?token=${encodeURIComponent(resolved)}`;
  const response = await staffFetch(url, { method: "GET", mode: "cors" });
  if (!response.ok) {
    throw new Error(`Provision status failed: ${response.status}`);
  }
  return response.json() as Promise<SuperadminCharityCreateResponse>;
}
